import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";

import { getAttachmentErrorKey, openAttachment } from "utils/attachment";
import { isAttachmentAuthenticationError, redirectAttachmentToLogin } from "utils/attachment-auth";

const useAttachmentActions = (scopeKey) => {
    const client = useApolloClient();
    const history = useHistory();
    const pending = useRef(new Map());
    const mounted = useRef(false);
    const [loading, setLoading] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        mounted.current = true;
        setLoading({});
        setErrors({});
        const operations = pending.current;
        return () => {
            mounted.current = false;
            operations.forEach(operation => {
                operation.controller.abort();
                if (operation.previewWindow && !operation.previewWindow.closed) {
                    operation.previewWindow.close();
                }
            });
            operations.clear();
        };
    }, [scopeKey]);

    const open = useCallback(async (attachment, options = {}) => {
        const id = String(attachment.id);
        if (pending.current.has(id)) return;

        const operation = { controller: new AbortController(), previewWindow: null };
        pending.current.set(id, operation);
        const isActive = () => mounted.current && pending.current.get(id) === operation;
        setLoading(previous => ({ ...previous, [id]: true }));
        setErrors(previous => ({ ...previous, [id]: null }));

        try {
            await openAttachment(client, attachment, {
                ...options,
                signal: operation.controller.signal,
                isActive,
                navigate: path => history.push(path),
                onPreviewWindow: previewWindow => { operation.previewWindow = previewWindow; },
            });
        } catch (error) {
            if (!isActive() || error.name === "AbortError") return;
            if (isAttachmentAuthenticationError(error)) {
                await redirectAttachmentToLogin(client, history, attachment.id);
            } else {
                setErrors(previous => ({ ...previous, [id]: getAttachmentErrorKey(error) }));
            }
        } finally {
            if (isActive()) {
                pending.current.delete(id);
                setLoading(previous => ({ ...previous, [id]: false }));
            }
        }
    }, [client, history]);

    return { open, loading, errors };
};

export default useAttachmentActions;
